import dayjs from 'dayjs';
import JSZip from 'jszip';
import { resolveUploadAssetUrl } from './resolveUploadAssetUrl';

/** 文件名中不允许的字符（Windows / macOS 通用） */
function sanitizeFilenamePart(s: string, fallback: string): string {
  const t = String(s || '').trim() || fallback;
  return t
    .replace(/[/\\:*?"<>|]/g, '_')
    .replace(/\s+/g, '_')
    .slice(0, 120);
}

function applyTimeToStamp(applyTime: string): string {
  const d1 = dayjs(applyTime);
  if (d1.isValid()) return d1.format('YYYYMMDD_HHmmss');
  const d2 = new Date(String(applyTime).replace(/-/g, '/'));
  if (!Number.isNaN(d2.getTime())) return dayjs(d2).format('YYYYMMDD_HHmmss');
  return dayjs().format('YYYYMMDD_HHmmss');
}

function toAbsoluteFetchUrl(resolved: string): string {
  if (/^https?:\/\//i.test(resolved)) return resolved;
  if (resolved.startsWith('/')) return `${window.location.origin}${resolved}`;
  return `${window.location.origin}/${resolved}`;
}

function extFromPathOrUrl(url: string): string {
  const clean = url.split('?')[0] || '';
  const m = clean.match(/\.([a-zA-Z0-9]{1,8})$/);
  return m ? `.${m[1].toLowerCase()}` : '';
}

/**
 * 将财务报销凭证打包为 zip，文件名：`{申请人}_{申请时间戳}.zip`。
 */
export async function downloadFinanceAttachmentsZip(opts: {
  applicant: string;
  applyTime: string;
  attachments: Array<{ name: string; url: string }>;
}): Promise<void> {
  const { applicant, applyTime, attachments } = opts;
  if (!attachments?.length) {
    throw new Error('没有可下载的附件');
  }

  const zip = new JSZip();
  const token = typeof window !== 'undefined' ? localStorage.getItem('token') : null;

  for (let i = 0; i < attachments.length; i++) {
    const att = attachments[i];
    const resolved = resolveUploadAssetUrl(att.url) || att.url;
    const fetchUrl = toAbsoluteFetchUrl(resolved);
    const res = await fetch(fetchUrl, {
      headers: token ? { Authorization: `Bearer ${token}` } : {}
    });
    if (!res.ok) {
      throw new Error(`无法下载：${att.name || `附件 ${i + 1}`}（HTTP ${res.status}）`);
    }
    const blob = await res.blob();
    let base = sanitizeFilenamePart(att.name || `凭证${i + 1}`, `凭证${i + 1}`);
    if (!/\.[a-zA-Z0-9]{1,8}$/.test(base)) {
      const ext = extFromPathOrUrl(att.url);
      if (ext) base += ext;
    }
    const entryName = `${String(i + 1).padStart(2, '0')}_${base}`;
    zip.file(entryName, blob);
  }

  const zipBase = `${sanitizeFilenamePart(applicant, '申请人')}_${applyTimeToStamp(applyTime)}`;
  const zipName = `${zipBase}.zip`;
  const out = await zip.generateAsync({ type: 'blob' });
  const a = document.createElement('a');
  a.href = URL.createObjectURL(out);
  a.download = zipName;
  a.click();
  URL.revokeObjectURL(a.href);
}
